import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isRTL, type LanguageCode } from '../i18n';

// Types
type Theme = 'light' | 'dark' | 'system';

interface SettingsContextType {
  // Theme
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // Language
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  isRTL: boolean;
  dir: 'ltr' | 'rtl';
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Storage keys
const STORAGE_KEYS = {
  THEME: 'app_theme',
  LANGUAGE: 'app_language',
};

// Get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

interface SettingsProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultLanguage?: LanguageCode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
  defaultTheme = 'system',
  defaultLanguage = 'en',
}) => {
  const { i18n } = useTranslation();

  // Initialize theme from localStorage or default
  const [theme, setThemeState] = useState<Theme>((): Theme => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    }
    return defaultTheme;
  });

  // Resolved theme (actual light/dark, not 'system')
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (theme === 'system') return getSystemTheme();
    return theme;
  });

  // Language state
  const [language, setLanguageState] = useState<LanguageCode>((): LanguageCode => {
    const lang = i18n.language;
    if (lang === 'en' || lang === 'ar') {
      return lang;
    }
    return defaultLanguage;
  });

  // Update resolved theme when theme changes or system preference changes
  useEffect(() => {
    const updateResolvedTheme = () => {
      const resolved = theme === 'system' ? getSystemTheme() : theme;
      setResolvedTheme(resolved);

      // Disable all CSS transitions during the class swap to prevent lag
      const root = document.documentElement;
      root.classList.add('disable-transitions');
      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
      // Re-enable transitions after a single frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.remove('disable-transitions');
        });
      });
    };

    updateResolvedTheme();

    // Listen for system theme changes
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => updateResolvedTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);

  // Update document direction and lang attribute when language changes
  useEffect(() => {
    const rtl = isRTL(language);
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Set theme handler
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEYS.THEME, newTheme);
  }, []);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
  }, [resolvedTheme, setTheme]);

  // Set language handler
  const setLanguage = useCallback(
    (lang: LanguageCode) => {
      setLanguageState(lang);
      i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
    },
    [i18n]
  );

  const value: SettingsContextType = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    language,
    setLanguage,
    isRTL: isRTL(language),
    dir: isRTL(language) ? 'rtl' : 'ltr',
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
